import { ddMMyyyyToDate, diffBetweenTwoYearsMs, isBigger } from '@/utils/date.utils'
import {
  isCPFromNavarra,
  isNumber,
  validatePhone,
  validateName,
  validateNameV2,
  validateNIF,
  validateNIE,
  validateCIF,
  validPassportFormat,
  validateEmail,
  validateMobile,
  validPolicyNumberFormat
} from '@/utils/validations.utils'

import { useCommonStore } from '@/modules/core/stores/commonStore'
import { MAX_CHARS_POLICY_NUMBER } from '@/modules/core/constants/common'

import { useI18n } from 'vue-i18n'

export function useRules() {
  const { t } = useI18n()

  const commonStore = useCommonStore()

  function required(value: string | number | boolean | Date | null, customText?: string) {
    const valid = !!value
    return valid || t(customText ?? 'common.validations.required')
  }

  function validateDate(value: string, customText?: string) {
    const dateTime = Number(ddMMyyyyToDate(value)?.getTime())
    const valid = !isNaN(dateTime)
    return valid || t(customText ?? 'common.validations.date')
  }

  function validateActuarialAge({
    date,
    minAge = 0,
    maxAge = 200,
    customMinText,
    customMaxText
  }: {
    date: string
    minAge?: number
    maxAge?: number
    customMinText?: string
    customMaxText?: string
  }): true | string {
    const birthDate = ddMMyyyyToDate(date)!
    birthDate.setHours(0, 0, 0, 0)

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let completedYears = today.getFullYear() - birthDate.getFullYear()
    const thisYearsBirthday = new Date(
      today.getFullYear(),
      birthDate.getMonth(),
      birthDate.getDate()
    )
    thisYearsBirthday.setHours(0, 0, 0, 0)
    if (today < thisYearsBirthday) {
      completedYears -= 1
    }

    const halfBirthday = new Date(
      birthDate.getFullYear() + completedYears,
      birthDate.getMonth() + 6,
      birthDate.getDate()
    )
    halfBirthday.setHours(0, 0, 0, 0)

    const actuarialAge = today >= halfBirthday ? completedYears + 1 : completedYears

    if (actuarialAge < minAge) {
      return t(customMinText ?? 'common.validations.min-age-holder')
    }
    if (actuarialAge > maxAge) {
      const key =
        actuarialAge > 115
          ? 'common.validations.max-age-over-115'
          : customMaxText ?? 'common.validations.max-age-common'

      return t(key, { years: maxAge })
    }

    return true
  }

  /**
   * actuarial
   *
   * @param value string
   * @param maxAge number
   * @param customText string (optional)
   * @returns boolean | string
   */
  function tooOldGreaterOrEqual(
    value: string,
    maxAge: number,
    customText?: string
  ): boolean | string {
    let valid = false

    const fechaLimite = ddMMyyyyToDate(value)
    if (!fechaLimite) {
      return t('common.validations.invalid-date')
    }
    if (fechaLimite) {
      const hoy = new Date()
      hoy.setHours(0, 0, 0, 0)

      const maxAgeDate = new Date(fechaLimite)
      maxAgeDate.setFullYear(fechaLimite.getFullYear() + maxAge + 1)

      // valid maxAge until next birthdate (maxAge 115 11 months...)
      valid = hoy < maxAgeDate
    }

    return (
      valid ||
      t(
        customText ??
          (maxAge >= 115
            ? 'common.validations.max-age-over-115'
            : 'common.validations.max-age-common'),
        { years: maxAge }
      )
    )
  }

  function tooOld(value: string, maxAge: number, startDate: Date | null, customText?: string) {
    const valid = !isBigger(ddMMyyyyToDate(value)!, maxAge, startDate ?? new Date())
    if (valid) return true

    if (maxAge >= 115) {
      return t('common.validations.max-age-over-115')
    }

    return t(customText ?? 'common.validations.max-age-common', { years: maxAge })
  }

  function tooYoung(value: string, minAge: number, startDate: Date | null, customText?: string) {
    const valid = isBigger(ddMMyyyyToDate(value)!, minAge, startDate ?? new Date())
    return valid || t(customText ?? 'common.validations.min-age-holder')
  }

  function futureDate(value: string | Date, customText?: string) {
    const date = typeof value === 'string' ? ddMMyyyyToDate(value)! : value

    const today = new Date()
    const dateToCompareWith = new Date(today.getFullYear(), today.getMonth(), 1)
    const diffMilliseconds = diffBetweenTwoYearsMs(date, dateToCompareWith)
    const valid = diffMilliseconds <= 0
    return valid || t(customText ?? 'common.validations.date')
  }

  function notFutureDate(value: string | Date, customText?: string) {
    const date = typeof value === 'string' ? ddMMyyyyToDate(value)! : value
    const diffMilliseconds = diffBetweenTwoYearsMs(date, new Date())
    const valid = diffMilliseconds >= 0
    return valid || t(customText ?? 'common.validations.date')
  }

  function isNotFromNavarra(value: string, customText?: string) {
    const valid = !isCPFromNavarra(value)
    return valid || t(customText ?? 'copy_validate_cp_navarra')
  }

  function yesNoRequired(value: number, customText?: string) {
    const valid = [0, 1].includes(value)
    return valid || t(customText ?? 'common.validations.option')
  }

  function isPositiveInteger(value: string, customText?: string) {
    const valid = isNumber(value) && Number(value) > 0
    return valid || t(customText ?? 'common.validations.date')
  }

  function isPositiveDecimal(value: string, customText?: string) {
    const valid = /^[0-9]+(?:\.[0-9]+)?$/.test(value)
    return (
      valid ||
      t(customText ?? 'hiring.common.insurance-body-measurements-form.weight-invalid-format')
    )
  }

  function maxValue(value: string | number, maxValue: number, customText?: string) {
    const valid = isNumber(String(value)) && Number(value) <= maxValue
    return valid || t(customText ?? 'copy_max-measure-value', { value: maxValue })
  }

  function maxValueMeasure(value: string | number, maxValue: number, measure: string) {
    const valid = isNumber(String(value)) && Number(value) <= maxValue
    return valid || t('copy_max-measure-value-complete', { value: maxValue, measure: measure })
  }

  function requiredFile(value: File[], customText?: string) {
    const valid = !!value && !!value?.length
    return valid || t(customText ?? 'common.validations.file-required')
  }

  function validName(value: string, customText?: string) {
    const valid = validateName(value)
    return valid || t(customText ?? 'common.validations.format')
  }

  function validNameV2(value: string, customText?: string) {
    const valid = validateNameV2(value, commonStore.getInvalidNames().join('|'))
    return valid || t(customText ?? 'common.validations.format')
  }

  function validSurname(value: string, customText?: string) {
    const valid = validateNameV2(value, commonStore.getInvalidSurnames().join('|'))
    return valid || t(customText ?? 'common.validations.format')
  }

  function validPhone(value: string, prefix: string, customText?: string) {
    const valid = validatePhone(value, prefix)
    return valid || t(customText ?? 'common.validations.format')
  }

  function validMobile(value: string, prefix: string, customText?: string) {
    const valid = validateMobile(value, prefix)
    return valid || t(customText ?? 'common.validations.format')
  }

  function validEmail(value: string, customText?: string) {
    const valid = validateEmail(value)
    return valid || t(customText ?? 'common.validations.email')
  }

  function characterLimit(value: string, limit: number, customText?: string) {
    const valid = value.length <= limit
    return valid || t(customText ?? 'common.validations.max-length')
  }

  function validNIF(value: string, customText?: string) {
    const valid = validateNIF(value)
    return (
      valid || t(customText ?? 'hiring.common.insurance-another-company-data.document-not-valid')
    )
  }

  function validNIE(value: string, customText?: string) {
    const valid = validateNIE(value)
    return (
      valid || t(customText ?? 'hiring.common.insurance-another-company-data.document-not-valid')
    )
  }

  function validCIF(value: string, customText?: string) {
    const valid = validateCIF(value)
    return (
      valid || t(customText ?? 'hiring.common.insurance-another-company-data.document-not-valid')
    )
  }

  function validPassport(value: string, customText?: string) {
    const valid = validPassportFormat(value)
    return valid || t(customText ?? 'common.validations.passport-error-not-holder')
  }

  function validPolicyNumber(value: string, customText?: string) {
    const valid = validPolicyNumberFormat(value)
    return valid || t(customText ?? 'copy_policy_number_validation', { n: MAX_CHARS_POLICY_NUMBER })
  }

  function dateHigherOrEquals(value: string, dateToCompare: Date, customText?: string) {
    const valid = diffBetweenTwoYearsMs(dateToCompare, ddMMyyyyToDate(value)!) >= 0
    return valid || t(customText ?? 'common.validations.min-expiration-date')
  }

  /**
   * chip format validator based on ISO 11784/11785
   *
   * @param value string | number
   * @returns boolean | string
   */
  function validateChipFormat(value: string | number) {
    const v = String(value)
    if (!value || !v?.trim()) {
      return t('pets.chip-require')
    }

    const chipRegex = /^\d{15}$/ // 15 numeric digits
    if (!chipRegex.test(v)) {
      return t('pets.chip-not-valid-format')
    }

    // Validate country code (first 3 digits must be in a valid range)
    const countryCode = parseInt(v.substring(0, 3), 10)
    if (!isValidChipCountryCode(countryCode)) {
      return t('pets.chip-not-valid-format')
    }

    return true // valid
  }

  /**
   * to validate country codes and manufacturers according to ISO 11784
   *
   * @param value number
   * @returns boolean
   */
  function isValidChipCountryCode(value: number) {
    return (
      (value >= 1 && value <= 899) || // Official countries according to ISO 3166-1
      (value >= 900 && value <= 999) || // Commercial manufacturers
      value === 0o0 // Unassigned (valid according to standard)
    )
  }

  return {
    required,
    validateDate,
    tooOld,
    tooOldGreaterOrEqual,
    validateActuarialAge,
    tooYoung,
    futureDate,
    notFutureDate,
    isNotFromNavarra,
    yesNoRequired,
    isPositiveInteger,
    isPositiveDecimal,
    maxValue,
    maxValueMeasure,
    requiredFile,
    validName,
    validNameV2,
    validSurname,
    validPhone,
    validMobile,
    validEmail,
    characterLimit,
    validCIF,
    validNIE,
    validNIF,
    validPassport,
    validPolicyNumber,
    dateHigherOrEquals,
    validateChipFormat
  }
}
